import { supabase } from './supabase';

const toCamel = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamel);
  return Object.fromEntries(
    Object.entries(obj)?.map(([k, v]) => [
      k?.replace(/_([a-z])/g, (_, c) => c?.toUpperCase()),
      toCamel(v)
    ])
  );
};

const toSnake = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnake);
  return Object.fromEntries(
    Object.entries(obj)?.map(([k, v]) => [
      k?.replace(/([A-Z])/g, '_$1')?.toLowerCase(),
      v
    ])
  );
};

// ============================================================
// RESELLER WORKSPACES SERVICE
// ============================================================
export const workspaceService = {
  async getMyWorkspace() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: { message: 'Not authenticated' } };
      const { data, error } = await supabase
        ?.from('reseller_workspaces')
        ?.select('*')
        ?.eq('reseller_id', user?.id)
        ?.maybeSingle();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getAllWorkspaces() {
    try {
      const { data, error } = await supabase
        ?.from('reseller_workspaces')
        ?.select('*, reseller:user_profiles(id, full_name, email)')
        ?.order('created_at', { ascending: false });
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async create(workspaceData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const payload = toSnake({ ...workspaceData, resellerId: user?.id });
      const { data, error } = await supabase
        ?.from('reseller_workspaces')
        ?.insert(payload)
        ?.select()
        ?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async update(id, workspaceData) {
    try {
      const { data, error } = await supabase
        ?.from('reseller_workspaces')
        ?.update(toSnake(workspaceData))
        ?.eq('id', id)
        ?.select()
        ?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getStats(workspaceId) {
    try {
      const [websitesRes, membersRes] = await Promise.all([
        supabase?.from('workspace_websites')?.select('status')?.eq('workspace_id', workspaceId),
        supabase?.from('workspace_members')?.select('role')?.eq('workspace_id', workspaceId)?.eq('is_active', true)
      ]);
      return {
        data: {
          totalWebsites: websitesRes?.data?.length || 0,
          activeWebsites: websitesRes?.data?.filter(w => w?.status === 'active')?.length || 0,
          totalMembers: membersRes?.data?.length || 0,
          admins: membersRes?.data?.filter(m => m?.role === 'admin')?.length || 0,
          editors: membersRes?.data?.filter(m => m?.role === 'editor')?.length || 0,
        },
        error: null
      };
    } catch (error) {
      return { data: { totalWebsites: 0, activeWebsites: 0, totalMembers: 0, admins: 0, editors: 0 }, error };
    }
  }
};

// ============================================================
// WORKSPACE WEBSITES SERVICE
// ============================================================
export const websiteService = {
  async getByWorkspace(workspaceId) {
    try {
      const { data, error } = await supabase
        ?.from('workspace_websites')
        ?.select('*, created_by_user:user_profiles!workspace_websites_created_by_fkey(id, full_name)')
        ?.eq('workspace_id', workspaceId)
        ?.order('created_at', { ascending: false });
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async create(workspaceId, websiteData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const payload = toSnake({ ...websiteData, workspaceId, createdBy: user?.id });
      const { data, error } = await supabase
        ?.from('workspace_websites')
        ?.insert(payload)
        ?.select()
        ?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async update(id, websiteData) {
    try {
      const { data, error } = await supabase
        ?.from('workspace_websites')
        ?.update(toSnake(websiteData))
        ?.eq('id', id)
        ?.select()
        ?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('workspace_websites')?.delete()?.eq('id', id);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  }
};

// ============================================================
// WORKSPACE MEMBERS SERVICE
// ============================================================
export const memberService = {
  async getByWorkspace(workspaceId) {
    try {
      const { data, error } = await supabase
        ?.from('workspace_members')
        ?.select('*, user:user_profiles(id, full_name, email, avatar_url, is_active), invited_by_user:user_profiles!workspace_members_invited_by_fkey(id, full_name)')
        ?.eq('workspace_id', workspaceId)
        ?.order('invited_at', { ascending: false });
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async inviteByEmail(workspaceId, email, role = 'editor') {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      // Find user by email
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('email', email)
        ?.maybeSingle();
      if (profileError) throw profileError;
      if (!profile) return { data: null, error: { message: 'No user found with that email address. They must sign up first.' } };

      const { data, error } = await supabase
        ?.from('workspace_members')
        ?.insert({ workspace_id: workspaceId, user_id: profile?.id, role, invited_by: user?.id })
        ?.select()
        ?.single();
      if (error) {
        if (error?.code === '23505') return { data: null, error: { message: 'This user is already a member of your workspace.' } };
        throw error;
      }
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateRole(memberId, role) {
    try {
      const { data, error } = await supabase
        ?.from('workspace_members')
        ?.update({ role })
        ?.eq('id', memberId)
        ?.select()
        ?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async remove(memberId) {
    try {
      const { error } = await supabase?.from('workspace_members')?.delete()?.eq('id', memberId);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  }
};
